import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { SelectInput } from '@/components/SelectInput';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCompanyStore } from '@/stores/useCompanyStore';
import { useUserStore } from '@/stores/useUserStore';
import { Company } from '@/types/company';
import { User } from '@/types/user';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBuildingUser, FaUserTie } from 'react-icons/fa6';

interface CreateCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateForSelf: () => void;
  onCreateForUser: (userId: number) => void;
}

export const CreateCardModal = ({
  isOpen,
  onClose,
  onCreateForSelf,
  onCreateForUser,
}: CreateCardModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { listCompanies, companies } = useCompanyStore();
  const { listUsers, users } = useUserStore();

  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [step, setStep] = useState<'initial' | 'selectUser'>('initial');

  // Reset inputs when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCompany(null);
      setSelectedUser(null);
      setStep('initial');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (user?.role === 'admin') {
        listCompanies({});
      } else if (user?.role === 'manager' && user.company?.id) {
        listUsers({ company_id: user.company.id });
      }
    }
  }, [isOpen, user?.role, user?.company?.id, listCompanies, listUsers]);

  useEffect(() => {
    if (selectedCompany) {
      listUsers({ company_id: selectedCompany });
    }
  }, [selectedCompany, listUsers]);

  const handleCompanySelect = (e: {
    target: {
      name: string;
      value: string | number | boolean | (string | number | boolean)[];
    };
  }) => {
    setSelectedCompany(Number(e.target.value));
    setSelectedUser(null);
  };

  const handleUserSelect = (e: {
    target: {
      name: string;
      value: string | number | boolean | (string | number | boolean)[];
    };
  }) => {
    setSelectedUser(Number(e.target.value));
  };

  const handleClose = () => {
    setSelectedCompany(null);
    setSelectedUser(null);
    setStep('initial');
    onClose();
  };

  const handleCreateForCompany = () => {
    if (selectedUser) {
      onCreateForUser(selectedUser);
      handleClose();
    }
  };

  const renderContent = () => {
    if (step === 'initial') {
      return (
        <div className="flex flex-col gap-4 md:grid md:grid-cols-2">
          {/* Option pour soi-même */}
          <div
            onClick={() => {
              onCreateForSelf();
              handleClose();
            }}
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border p-4 transition-colors duration-300 hover:bg-linko-light"
          >
            <FaUserTie className="size-8" />
            <span>{t('createCardModal:forSelf')}</span>
          </div>

          {/* Option pour entreprise/autres utilisateurs */}
          <div
            onClick={() => setStep('selectUser')}
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border p-4 transition-colors duration-300 hover:bg-linko-light"
          >
            <FaBuildingUser className="size-8" />
            <span>{t('createCardModal:forCompany')}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {user?.role === 'admin' && (
          <SelectInput
            label={t('createCardModal:selectCompany')}
            options={companies.map((company: Company) => ({
              value: company.id.toString(),
              label: company.name,
            }))}
            name="company"
            defaultValue={selectedCompany?.toString() || ''}
            onChange={handleCompanySelect}
            useFormik={false}
          />
        )}

        {(user?.role === 'manager' || selectedCompany) && (
          <SelectInput
            label={t('createCardModal:selectUser')}
            options={users.map((user: User) => ({
              value: user.id.toString(),
              label: user.firstname + ' ' + user.lastname,
            }))}
            name="user"
            defaultValue={selectedUser?.toString() || ''}
            onChange={handleUserSelect}
            useFormik={false}
          />
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={() => setStep('initial')}>{t('common:back')}</Button>
          <Button onClick={handleCreateForCompany} disabled={!selectedUser}>
            {t('createCardModal:create')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('createCardModal:title')}
      className="w-full max-w-md"
    >
      {renderContent()}
    </Modal>
  );
};
